import { getRouteApi, Link } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import DataTable from '~/components/DataTable';
import { UserProfilePicture } from '~/components/UserProfilePicture';
import Flag from '~/partials/Flag';
import { ClanTags, LevelTags } from '~/partials/Tags';
import { useLocaleString } from '~/util/hooks/useLocale';
import { LeaderboardUserEntry } from '~/util/types/leaderboard';

const Route = getRouteApi('/leaderboard/');

export const LeaderboardTableUsers = () => {
    const data = Route.useLoaderData();
    const { country, sort, list } = Route.useSearch({
        select: ({ country, sort, list }) => ({
            country,
            sort,
            list,
        }),
    });

    const localeString = useLocaleString();

    const cellClassNames = (cell: { column: { id: string } }) =>
        `!clamp-[text-xs-base-clamp] p-2 ${
            (cell.column.id === 'country' ||
                cell.column.id === 'hardest' ||
                cell.column.id === 'extremes' ||
                cell.column.id === 'members_count' ||
                cell.column.id === 'points') &&
            'hidden lg:table-cell'
        } `;

    const usercolumns: ColumnDef<LeaderboardUserEntry>[] = [
        {
            accessorKey: 'rank',
            header: 'Rank',
            cell: ({ row }) => {
                const entry = row.original;
                const rank =
                    '#' +
                    (sort === 'TotalPoints'
                        ? country === 0
                            ? entry.rank
                            : entry.country_rank
                        : sort === 'RawPoints'
                          ? country === 0
                              ? entry.raw_rank
                              : entry.country_raw_rank
                          : sort === 'ExtremeCount'
                            ? country === 0
                                ? entry.extremes_rank
                                : entry.country_extremes_rank
                            : sort === 'Hardest'
                              ? country === 0
                                  ? entry.hardest_rank
                                  : entry.country_hardest_rank
                              : '-');

                return <h2 className="clamp-[text-lg-2xl-clamp]">{rank}</h2>;
            },
        },
        {
            accessorKey: 'user.global_name',
            header: 'Player',
            cell: ({ row }) => {
                const user = row.original.user;
                const clan = row.original.clan;
                const flag = <Flag countryCode={row.original.country} />;

                return (
                    <>
                        <div className="flex items-center gap-x-2">
                            <UserProfilePicture
                                user={user}
                                className="size-9 mr-1 z-100"
                            />
                            {row.original.country ? (
                                <Link
                                    className="transition-transform hover:scale-110 block w-max"
                                    to="/profile/country/$country"
                                    params={{
                                        country: String(row.original.country),
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {flag}
                                </Link>
                            ) : (
                                flag
                            )}
                            <div className="hidden lg:flex items-center">
                                {clan && (
                                    <ClanTags
                                        item={clan}
                                        className="text-[1.3rem] font-bold text-secondary-foreground bg-transparent p-1"
                                    />
                                )}

                                <h3 className="font-bold text-[1.3rem]">
                                    {row.original?.user.global_name}
                                </h3>
                            </div>
                            {/* mobile */}
                            <div className="flex-col lg:hidden py-[0.2rem]">
                                <div className="flex items-center">
                                    {clan && (
                                        <ClanTags
                                            item={clan}
                                            className="text-[1.1rem] font-bold text-secondary-foreground bg-transparent p-1"
                                        />
                                    )}
                                    <h3 className="font-bold text-[1.1rem]">
                                        {row.original?.user.global_name}
                                    </h3>
                                </div>
                                <div className="h-1" />
                                <h3 className="text-secondary-light text-[1.2em]">
                                    {localeString(
                                        sort === 'RawPoints'
                                            ? (row.original.total_points -
                                                  row.original.pack_points) /
                                                  10
                                            : row.original.total_points / 10,
                                    )}{' '}
                                    pts
                                </h3>
                            </div>
                        </div>
                    </>
                );
            },
        },
        {
            id: 'points',
            accessorKey: 'points',
            header: sort === 'RawPoints' ? 'Points' : 'Points (with packs)',
            cell: ({ row }) => {
                const entry = row.original;
                const number =
                    sort === 'RawPoints'
                        ? (entry.total_points - entry.pack_points) / 10
                        : entry.total_points / 10;

                return (
                    <h3 className="text-secondary-light font-bold text-[1.2em]">
                        {localeString(number)} points
                    </h3>
                );
            },
        },
        {
            accessorKey: 'hardest',
            header: 'Hardest',
            cell: ({ row }) => {
                const hardest = row.original.hardest;
                return hardest ? (
                    <LevelTags
                        item={hardest}
                        className="font-bold text-[1.2rem] text-secondary-light p-1 bg-transparent"
                    />
                ) : (
                    <h3 className="text-secondary-light font-bold text-[1.2rem]">
                        -
                    </h3>
                );
            },
        },
        {
            accessorKey: 'extremes',
            header: 'Extremes count',
            cell: ({ row }) => {
                const str = row.original.extremes
                    ? `${row.original.extremes} extreme${row.original.extremes === 1 ? '' : 's'}`
                    : '-';

                return (
                    <h3 className="text-secondary-light font-bold text-[1.2rem]">
                        {str}
                    </h3>
                );
            },
        },
    ];

    return (
        <DataTable
            columns={usercolumns}
            data={(data?.data as LeaderboardUserEntry[]) || []}
            styledHeader
            cellClassName={cellClassNames}
            headerClassName={cellClassNames}
            rowClassName={() => 'h-5'}
            onRowLink={({ data }) => ({
                to: '/profile/user/$userid',
                params: {
                    userid: data.user.username,
                },
                search: { list },
            })}
        />
    );
};
